import React, { useState, useEffect, useCallback } from 'react';
import { api, useLanguage } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Pill, Check, Sun, Moon, Clock, Zap, Dumbbell, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TIMING_META = {
  matin: { label: 'Matin', icon: Sun, color: 'text-amber-400' },
  midi: { label: 'Midi', icon: Clock, color: 'text-blue-400' },
  pre_workout: { label: 'Pre', icon: Zap, color: 'text-orange-400' },
  post_workout: { label: 'Post', icon: Dumbbell, color: 'text-emerald-400' },
  soir: { label: 'Soir', icon: Moon, color: 'text-indigo-400' },
};

export default function SupplementsWidget() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const navigate = useNavigate();
  const [stack, setStack] = useState([]);
  const [todayIds, setTodayIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [protRes, todayRes] = await Promise.all([
        api.get('/supplements/protocol'),
        api.get('/supplements/today'),
      ]);
      setStack(protRes.data.protocol?.supplements || []);
      setTodayIds(todayRes.data.taken_ids || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = async (supId) => {
    try {
      const res = await api.post('/supplements/log', { supplement_id: supId });
      if (res.data.toggled_off) {
        setTodayIds(prev => prev.filter(id => id !== supId));
      } else {
        setTodayIds(prev => [...prev, supId]);
      }
    } catch { /* silent */ }
  };

  if (loading || stack.length === 0) return null;

  const taken = stack.filter(s => todayIds.includes(s.id)).length;
  const total = stack.length;
  const pct = total > 0 ? Math.round((taken / total) * 100) : 0;

  return (
    <Card className="border-0 bg-card/60 shadow-lg" data-testid="dashboard-supplements-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Pill className="w-4 h-4 text-indigo-400" />
            <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              {fr ? 'Supplements' : 'Supplements'}
            </span>
          </div>
          <button onClick={() => navigate('/supplements')} className="text-[9px] text-indigo-400 font-bold flex items-center gap-0.5 hover:underline" data-testid="supplements-widget-link">
            {fr ? 'Voir tout' : 'See all'} <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Mini progress bar */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-bold text-muted-foreground">{taken}/{total}</span>
        </div>

        {/* Compact list — show max 5 */}
        <div className="space-y-1.5">
          {stack.slice(0, 5).map(s => {
            const isTaken = todayIds.includes(s.id);
            const meta = TIMING_META[s.timing] || TIMING_META.matin;
            const TimingIcon = meta.icon;
            return (
              <div key={s.id} className={`flex items-center gap-2.5 p-2 rounded-lg transition-all cursor-pointer ${isTaken ? 'bg-emerald-500/8' : 'bg-white/[0.02] hover:bg-white/[0.04]'}`}
                onClick={() => handleToggle(s.id)} data-testid={`widget-supplement-${s.id}`}>
                <div className={`w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center shrink-0 ${isTaken ? 'bg-emerald-500 border-emerald-500' : 'border-muted-foreground/30'}`}>
                  {isTaken && <Check className="w-3 h-3 text-white" />}
                </div>
                <TimingIcon className={`w-3 h-3 ${meta.color} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate leading-tight ${isTaken ? 'text-emerald-400 line-through' : 'text-foreground'}`}>
                    {s.name || (fr ? 'Supplement' : 'Supplement')}
                  </p>
                  {s.dosage && (
                    <p className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5">
                      {s.dosage}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {stack.length > 5 && (
            <p className="text-[9px] text-muted-foreground text-center pt-1">+{stack.length - 5} {fr ? 'de plus' : 'more'}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
